import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as InfoIcon } from "feather-icons/dist/icons/info.svg";
import { ReactComponent as ZapIcon } from "feather-icons/dist/icons/zap.svg";
import { ReactComponent as AlertIcon } from "feather-icons/dist/icons/alert-triangle.svg";

import { renderInline } from "../utils/inline";
import { slugify, stripInlineMarkup } from "../utils/text";

/**
 * Rendu du corps d'un article à partir de ses blocs (voir src/blog/README.md).
 * Types : lead · h2 · h3 · p · ul · ol · quote · callout · keyfacts · image · code · table
 */

/** Attribue un id unique à chaque titre (h2/h3) — partagé avec le sommaire. */
export const withHeadingIds = content => {
  const used = new Set();
  return (content || []).map(block => {
    if (block.type !== "h2" && block.type !== "h3") return block;
    const base = slugify(stripInlineMarkup(block.text)) || "section";
    let id = base;
    let n = 2;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);
    return { ...block, id: block.id || id };
  });
};

export const extractHeadings = content =>
  withHeadingIds(content)
    .filter(b => b.type === "h2" || b.type === "h3")
    .map(b => ({ id: b.id, text: stripInlineMarkup(b.text), level: b.type === "h2" ? 2 : 3 }));

const Prose = styled.div`
  ${tw`text-gray-800 text-base lg:text-lg leading-relaxed`}

  p {
    ${tw`mt-6`}
  }
  h2 {
    ${tw`mt-12 text-2xl sm:text-3xl font-black text-gray-900 leading-tight tracking-wide`}
    scroll-margin-top: 6rem;
  }
  h3 {
    ${tw`mt-10 text-xl sm:text-2xl font-bold text-gray-900 leading-snug`}
    scroll-margin-top: 6rem;
  }
  h2 + p,
  h3 + p {
    ${tw`mt-4`}
  }
  ul,
  ol {
    ${tw`mt-6 pl-6`}
  }
  ul {
    ${tw`list-disc`}
  }
  ol {
    ${tw`list-decimal`}
  }
  li {
    ${tw`mt-2 pl-1`}
  }
  li::marker {
    ${tw`text-primary-500 font-bold`}
  }
  a {
    ${tw`text-primary-500 font-bold border-b-2 border-transparent hocus:border-primary-500 hocus:text-primary-800 transition duration-300`}
  }
  strong {
    ${tw`font-bold text-gray-900`}
  }
  em {
    ${tw`italic`}
  }
  code {
    ${tw`font-mono text-sm bg-gray-200 text-primary-900 rounded px-1`}
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
  }
  pre {
    ${tw`mt-8 p-6 rounded-lg bg-gray-900 text-gray-100 overflow-x-auto text-sm leading-relaxed`}
  }
  pre code {
    ${tw`bg-transparent text-gray-100 p-0 rounded-none`}
  }
  sup.cite {
    ${tw`ml-px text-xs leading-none`}
    vertical-align: super;
  }
  sup.cite a {
    ${tw`font-bold text-primary-500 border-0`}
  }
  blockquote {
    ${tw`mt-8 pl-6 border-l-4 border-primary-500 text-lg lg:text-xl text-gray-700 italic font-medium`}
  }
  blockquote cite {
    ${tw`block mt-3 text-sm not-italic text-gray-600 font-semibold`}
  }
  figure {
    ${tw`mt-8`}
  }
  figure img {
    ${tw`w-full rounded-lg`}
  }
  figcaption {
    ${tw`mt-3 text-sm text-gray-600 text-center font-medium`}
  }
  table {
    ${tw`mt-8 w-full text-sm lg:text-base border-collapse`}
  }
  th {
    ${tw`text-left bg-gray-200 font-bold p-3 text-gray-900`}
  }
  td {
    ${tw`p-3 border-b-2 border-gray-200 align-top`}
  }
  .table-wrap {
    ${tw`overflow-x-auto`}
  }
`;

const Lead = tw.p`text-lg sm:text-xl lg:text-2xl text-gray-700 font-medium leading-relaxed`;

const Callout = styled.aside(({ $tone }) => [
  tw`mt-8 rounded-lg p-6 sm:p-8 border-l-4`,
  $tone === "tip" && tw`bg-green-100 border-green-500`,
  $tone === "warning" && tw`bg-orange-100 border-orange-500`,
  ($tone === "info" || !$tone) && tw`bg-gray-100 border-primary-500`,
]);
const CalloutTitle = styled.p`
  ${tw`flex items-center uppercase text-xs font-bold tracking-widest text-gray-900`}
  svg {
    ${tw`w-4 h-4 mr-2 text-primary-500`}
  }
`;
const CalloutText = styled.div`
  ${tw`mt-3 text-base text-gray-800`}
  & > p {
    ${tw`mt-0!`}
  }
  & > ul {
    ${tw`mt-3!`}
  }
`;

const KeyFacts = tw.aside`mt-8 rounded-lg bg-primary-900 text-gray-100 p-6 sm:p-8`;
const KeyFactsTitle = tw.p`uppercase text-xs font-bold tracking-widest text-primary-100`;
const KeyFactsGrid = tw.dl`mt-4 grid gap-4 sm:grid-cols-2`;
const KeyFactLabel = tw.dt`text-xs uppercase tracking-wide text-gray-400 font-semibold`;
const KeyFactValue = tw.dd`mt-1 font-bold text-base lg:text-lg leading-snug`;

const toneIcon = { tip: ZapIcon, warning: AlertIcon, info: InfoIcon };

const Block = ({ block, options }) => {
  const inline = text => renderInline(text, options);

  switch (block.type) {
    case "lead":
      return <Lead>{inline(block.text)}</Lead>;
    case "h2":
      return <h2 id={block.id}>{inline(block.text)}</h2>;
    case "h3":
      return <h3 id={block.id}>{inline(block.text)}</h3>;
    case "p":
      return <p>{inline(block.text)}</p>;
    case "ul":
      return (
        <ul>
          {(block.items || []).map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol>
          {(block.items || []).map((item, i) => (
            <li key={i}>{inline(item)}</li>
          ))}
        </ol>
      );
    case "quote":
      return (
        <blockquote>
          <p>{inline(block.text)}</p>
          {block.cite && <cite>— {inline(block.cite)}</cite>}
        </blockquote>
      );
    case "callout": {
      const Icon = toneIcon[block.tone] || InfoIcon;
      return (
        <Callout $tone={block.tone}>
          {block.title && (
            <CalloutTitle>
              <Icon aria-hidden="true" />
              {block.title}
            </CalloutTitle>
          )}
          <CalloutText>
            {block.text && <p>{inline(block.text)}</p>}
            {(block.items || []).length > 0 && (
              <ul>
                {block.items.map((item, i) => (
                  <li key={i}>{inline(item)}</li>
                ))}
              </ul>
            )}
          </CalloutText>
        </Callout>
      );
    }
    case "keyfacts":
      return (
        <KeyFacts aria-label={block.title || "En bref"}>
          <KeyFactsTitle>{block.title || "En bref"}</KeyFactsTitle>
          <KeyFactsGrid>
            {(block.facts || []).map((fact, i) => (
              <div key={i}>
                <KeyFactLabel>{fact.label}</KeyFactLabel>
                <KeyFactValue>{inline(fact.value)}</KeyFactValue>
              </div>
            ))}
          </KeyFactsGrid>
        </KeyFacts>
      );
    case "image":
      return (
        <figure>
          <img src={block.src} alt={block.alt || ""} loading="lazy" decoding="async" />
          {block.caption && <figcaption>{inline(block.caption)}</figcaption>}
        </figure>
      );
    case "code":
      return (
        <pre>
          <code className={block.lang ? `language-${block.lang}` : undefined}>{block.code}</code>
        </pre>
      );
    case "table":
      return (
        <div className="table-wrap">
          <table>
            {block.head && (
              <thead>
                <tr>
                  {block.head.map((cell, i) => (
                    <th key={i} scope="col">
                      {inline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {(block.rows || []).map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {block.caption && <figcaption>{inline(block.caption)}</figcaption>}
        </div>
      );
    default:
      return null;
  }
};

export default function ArticleBody({ content, seenCitations }) {
  const blocks = withHeadingIds(content);
  const options = { seenCitations };
  return (
    <Prose>
      {blocks.map((block, i) => (
        <Block key={i} block={block} options={options} />
      ))}
    </Prose>
  );
}
